import React, { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export type EditableFieldRowMode = 'editable' | 'display';
export type EditableFieldRowSize = 'default' | 'compact';

/** Review status from FinchGPT LOR review */
export interface FieldReviewInfo {
  /** Status: MATCH, EDIT, MISSING, VERIFY */
  status: string;
  /** FinchGPT's notes/complaint about the field */
  notes: string;
  /** Whether this field is flagged as problematic */
  isProblematic: boolean;
}

export interface EditableFieldRowProps {
  id: string;
  label: string;
  value: string;
  required?: boolean;
  /** Whether to display the "Optional" tag when required is false. Defaults to true. */
  showOptionalLabel?: boolean;
  /** Whether this row is the currently highlighted field. */
  active?: boolean;
  hoverKey?: string;
  onHover?: (key: string | null) => void;
  onChange?: (next: string) => void;
  onSave?: () => void;
  onCancel?: () => void;
  placeholder?: string;
  isSaving?: boolean;
  /** Whether transcript evidence exists for this field (enables snapping). */
  hasEvidence?: boolean;
  /** Visual/interaction mode. */
  mode?: EditableFieldRowMode;
  /** Spacing density. */
  size?: EditableFieldRowSize;
  /** If true, clicking the row (or pressing Enter/Space) enters edit mode. */
  activateOnRowClick?: boolean;
  /** Review status from FinchGPT - displays red highlight and tooltip when problematic */
  reviewInfo?: FieldReviewInfo;
}

export const EditableFieldRow: React.FC<EditableFieldRowProps> = ({
  id,
  label,
  value,
  required,
  active,
  hoverKey,
  onHover,
  onChange,
  onSave,
  onCancel,
  placeholder,
  isSaving,
  hasEvidence = false,
  mode = 'editable',
  size = 'compact',
  showOptionalLabel = true,
  activateOnRowClick = false,
  reviewInfo,
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleMouseEnter = () => onHover?.(hoverKey || id);

  const padding = size === 'compact' ? 'px-3 py-2' : 'px-4 py-3';

  // Check if field has problematic review status from FinchGPT
  const isProblematic = reviewInfo?.isProblematic ?? false;
  const hasReviewNotes = Boolean(reviewInfo?.notes);

  // Display-only mode never enters editing state and hides the edit affordance
  const isEditable = mode === 'editable';
  const showReadOnly = !isEditable || !isEditing;

  if (showReadOnly) {
    return (
      <div
        role={activateOnRowClick ? 'button' : 'group'}
        aria-label={`${label} ${required ? '(required)' : '(optional)'}${isProblematic ? ' - needs attention' : ''}`}
        onMouseEnter={handleMouseEnter}
        onFocus={handleMouseEnter}
        onClick={(e) => {
          e.stopPropagation();
          if (!isEditable || !activateOnRowClick) return;
          setIsEditing(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (!isEditable || !activateOnRowClick) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsEditing(true);
            setTimeout(() => inputRef.current?.focus(), 0);
          }
        }}
        tabIndex={activateOnRowClick ? 0 : -1}
        className={`group ${padding} transition-colors ${
          isProblematic
            ? 'border-l-4 border-l-red-500 bg-red-50/50 hover:bg-red-50/70'
            : active
              ? 'bg-indigo-50/50'
              : 'hover:bg-indigo-50/30'
        } ${activateOnRowClick ? 'cursor-pointer' : ''}`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`text-sm font-semibold truncate ${isProblematic ? 'text-red-800' : 'text-gray-900'}`}
            >
              {label}
            </span>
            {/* Show review status indicator with tooltip */}
            {isProblematic && hasReviewNotes ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    aria-label="FinchGPT flagged issue"
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 rounded cursor-help"
                  >
                    ⚠️ {reviewInfo?.status}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="max-w-xs bg-red-900 text-white border-red-700"
                >
                  <p className="text-xs font-medium">FinchGPT Review:</p>
                  <p className="text-xs mt-1">{reviewInfo?.notes}</p>
                </TooltipContent>
              </Tooltip>
            ) : isProblematic ? (
              <span
                aria-label="Needs attention"
                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 rounded"
              >
                ⚠️ {reviewInfo?.status}
              </span>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    aria-label={hasEvidence ? 'Has citation' : 'No citation'}
                    className={`inline-block h-1.5 w-1.5 rounded-full ${
                      hasEvidence ? 'bg-blue-400' : 'bg-gray-300'
                    }`}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  {hasEvidence ? 'Linked to evidence' : 'No match found'}
                </TooltipContent>
              </Tooltip>
            )}
            {!required && showOptionalLabel && (
              <span className="text-[10px] uppercase tracking-wide text-gray-500">
                Optional
              </span>
            )}
          </div>
          {isEditable && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                size="sm"
                variant="outline"
                className="text-xs"
                onClick={() => {
                  setIsEditing(true);
                  setTimeout(() => inputRef.current?.focus(), 0);
                }}
                aria-label={`Edit ${label}`}
              >
                Edit
              </Button>
            </div>
          )}
        </div>
        <div
          className={`mt-2 text-sm truncate underline decoration-2 underline-offset-[6px] ${
            isProblematic
              ? 'text-red-700 decoration-red-300/70'
              : hasEvidence
                ? 'text-gray-800 decoration-blue-300/70'
                : 'text-gray-500 decoration-gray-200'
          }`}
        >
          {value || <span className={isProblematic ? 'text-red-400' : 'text-gray-400'}>—</span>}
        </div>
      </div>
    );
  }

  // Editing state (only reachable when mode === 'editable')
  return (
    <div
      role="group"
      aria-label={`${label} ${required ? '(required)' : '(optional)'}`}
      onMouseEnter={handleMouseEnter}
      onFocus={handleMouseEnter}
      className={`${padding} transition-colors bg-indigo-50/40 ring-2 ring-indigo-200`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-gray-900 truncate">
            {label}
          </span>
          {!required && showOptionalLabel && (
            <span className="text-[10px] uppercase tracking-wide text-gray-500">
              Optional
            </span>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:gap-2">
        <div className="flex-1 min-w-0">
          <Input
            ref={inputRef}
            id={id}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder || `Enter ${label}`}
            aria-label={`${label} input`}
            className="h-9"
            disabled={isSaving}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSave?.();
                setIsEditing(false);
              }
              if (e.key === 'Escape') {
                onCancel?.();
                setIsEditing(false);
              }
            }}
          />
        </div>
        <div className="mt-2 sm:mt-0 sm:w-48 flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="text-xs"
            onClick={() => {
              onCancel?.();
              setIsEditing(false);
            }}
            aria-label={`Cancel ${label} edit`}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="text-xs"
            onClick={() => {
              onSave?.();
              setIsEditing(false);
            }}
            aria-label={`Update ${label}`}
            disabled={isSaving}
          >
            Update
          </Button>
        </div>
      </div>
    </div>
  );
};
